/**
 * Fee questionnaire answers. Amounts and pricing rules are owned by
 * booking-provider-api: pbt-ui asks GET /assessment-requests/fee-quote for the
 * total once every answer is known, and the booking is re-priced on submit.
 */

export const REGION_TYPE = {
  metro: 'metro',
  rural: 'rural',
} as const;

export const FEE_ASSESSMENT_TYPE = {
  adhd: 'adhd',
  autism: 'autism',
  combined: 'combined',
} as const;

export const PRESCRIPTION_TYPE = {
  gp: 'gp',
  telecare: 'telecare',
} as const;

export const SEEN_PSYCHIATRIST = {
  yes: 'yes',
  no: 'no',
} as const;

export const MEDICARE_TYPE = {
  medicare: 'medicare',
  dva: 'dva',
  none: 'none',
} as const;

/** Downstream ASD assessment amounts, shown on the ASD pathway card only. */
export const ASD_ASSESSMENT_FEE = 1034;
export const ASD_ASSESSMENT_REBATE = 268;

export type FeeAssessmentScope = (typeof FEE_ASSESSMENT_TYPE)[keyof typeof FEE_ASSESSMENT_TYPE];
export type Region = (typeof REGION_TYPE)[keyof typeof REGION_TYPE];
export type Prescription = (typeof PRESCRIPTION_TYPE)[keyof typeof PRESCRIPTION_TYPE];
export type SeenPsychiatrist = (typeof SEEN_PSYCHIATRIST)[keyof typeof SEEN_PSYCHIATRIST];
export type MedicareType = (typeof MEDICARE_TYPE)[keyof typeof MEDICARE_TYPE];

export const FEE_ASSESSMENT_SCOPE_VALUES = [
  FEE_ASSESSMENT_TYPE.adhd,
  FEE_ASSESSMENT_TYPE.autism,
  FEE_ASSESSMENT_TYPE.combined,
] as const;

export const REGION_TYPE_VALUES = [
  REGION_TYPE.metro,
  REGION_TYPE.rural,
] as const;

export const PRESCRIPTION_TYPE_VALUES = [
  PRESCRIPTION_TYPE.gp,
  PRESCRIPTION_TYPE.telecare,
] as const;

export const SEEN_PSYCHIATRIST_VALUES = [
  SEEN_PSYCHIATRIST.yes,
  SEEN_PSYCHIATRIST.no,
] as const;

/** The patient's answers on the fee questionnaire. */
export interface FeeSelection {
  location: Region | '';
  feeAssessmentScope: FeeAssessmentScope | '';
  prescriptionType: Prescription | '';
  seenPsychiatrist: SeenPsychiatrist | '';
}

/** What the server quotes for a complete set of answers. */
export interface FeeQuote {
  gross: number;
  /** Combined + Metro: the ASD assessment is billed separately afterwards. */
  hasAsdPathway: boolean;
  /** The $300 recent psychiatric history review is part of `gross`. */
  includesPsychiatricReviewFee: boolean;
}

export const isAutismAssessment = (
  feeAssessmentScope: FeeAssessmentScope | '',
): boolean => feeAssessmentScope === FEE_ASSESSMENT_TYPE.autism;

/**
 * True for Medicare or DVA card holders. Patients with neither get no
 * Medicare rebate or bulk billing, so rebate wording is not shown to them.
 */
export const hasMedicareOrDvaCard = (medicareType: string): boolean => (
  medicareType === MEDICARE_TYPE.medicare || medicareType === MEDICARE_TYPE.dva
);

/** Maps the details-page answer to the card type the server prices on. */
export const toMedicareType = (medicareType: string): MedicareType | '' => (
  Object.values(MEDICARE_TYPE).includes(medicareType as MedicareType)
    ? medicareType as MedicareType
    : ''
);

/** Returns true when every answer that affects the fee is present. */
export const hasCompleteFeeInputs = (selection: FeeSelection): boolean => {
  const prescriptionAnswered = isAutismAssessment(selection.feeAssessmentScope)
    || Boolean(selection.prescriptionType);
  return Boolean(
    selection.location
    && selection.feeAssessmentScope
    && selection.seenPsychiatrist
    && prescriptionAnswered,
  );
};
